import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatCapabilityLabel } from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function fieldName(field) {
  return field?.name || field?.key || '';
}

function fieldLabel(field) {
  return field?.label || field?.display_name || fieldName(field);
}

function renderValue(value) {
  if (value === null || typeof value === 'undefined' || value === '') return 'Not supplied';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function formatTimestamp(value) {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export default function NeedOpportunityDetailPage(props) {
  const { requestId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [providerBusinessUnitId, setProviderBusinessUnitId] = useState('');
  const [selectedCapabilityIds, setSelectedCapabilityIds] = useState([]);
  const [dependencyNotes, setDependencyNotes] = useState({});
  const [explanation, setExplanation] = useState('');
  const [sequencingNotes, setSequencingNotes] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/requests/need-opportunities/${encodeURIComponent(requestId)}`);
      const nextDetail = response.data || null;
      setDetail(nextDetail);
      const units = Array.isArray(nextDetail?.eligible_provider_business_units) ? nextDetail.eligible_provider_business_units : [];
      setProviderBusinessUnitId((current) => current || (units.length === 1 ? units[0].business_unit_id : ''));
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Unable to load need-based opportunity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [requestId]);

  useEffect(() => {
    setSelectedCapabilityIds([]);
    setDependencyNotes({});
  }, [providerBusinessUnitId]);

  const request = detail?.request || {};
  const metadata = request.metadata || {};
  const providerUnits = Array.isArray(detail?.eligible_provider_business_units) ? detail.eligible_provider_business_units : [];
  const capabilities = useMemo(
    () => (Array.isArray(detail?.eligible_capabilities) ? detail.eligible_capabilities : [])
      .filter((capability) => capability.provider_business_unit_id === providerBusinessUnitId),
    [detail, providerBusinessUnitId],
  );
  const selectedCapabilities = selectedCapabilityIds
    .map((id) => capabilities.find((capability) => capability.capability_definition_id === id))
    .filter(Boolean);
  const fields = Array.isArray(detail?.shared_fields) ? detail.shared_fields : [];
  const values = detail?.need_request_values || {};
  const artifacts = Array.isArray(detail?.general_artifacts) ? detail.general_artifacts : [];
  const proposals = Array.isArray(detail?.proposals) ? detail.proposals : [];
  const providerHasSubmitted = proposals.some((proposal) => (
    proposal.provider_business_unit_id === providerBusinessUnitId && proposal.status === 'submitted'
  ));
  const canSubmit = Boolean(detail?.authorization?.can_submit_proposal) && !providerHasSubmitted;

  const toggleCapability = (capabilityId, checked) => {
    setSelectedCapabilityIds((current) => (
      checked ? [...current, capabilityId] : current.filter((id) => id !== capabilityId)
    ));
  };

  const moveCapability = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= selectedCapabilityIds.length) return;
    setSelectedCapabilityIds((current) => {
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const submitProposal = async () => {
    if (!providerBusinessUnitId) {
      setError('Select the provider Business Unit submitting this proposal.');
      return;
    }
    if (!selectedCapabilityIds.length) {
      setError('Select at least one Capability for the proposed solution.');
      return;
    }
    if (!explanation.trim()) {
      setError('Explain the proposed fulfillment approach.');
      return;
    }
    setSubmitting(true);
    setError('');
    setFeedback('');
    try {
      await axiosInstance.post(`/requests/need-opportunities/${encodeURIComponent(requestId)}/proposals`, {
        provider_business_unit_id: providerBusinessUnitId,
        explanation: explanation.trim(),
        sequencing_notes: sequencingNotes.trim() || undefined,
        capabilities: selectedCapabilityIds.map((capabilityId, index) => ({
          capability_definition_id: capabilityId,
          sequence_order: index + 1,
          dependency_notes: dependencyNotes[capabilityId]?.trim() || undefined,
        })),
      });
      setFeedback('Capability proposal submitted. Other eligible providers may still submit proposals.');
      setExplanation('');
      setSequencingNotes('');
      setSelectedCapabilityIds([]);
      setDependencyNotes({});
      await load();
    } catch (submitError) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Unable to submit Capability proposal.');
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawProposal = async (proposalId) => {
    if (!window.confirm('Withdraw this proposal?')) return;
    setSubmitting(true);
    setError('');
    try {
      await axiosInstance.post(`/requests/need-proposals/${encodeURIComponent(proposalId)}/withdraw`);
      setFeedback('Proposal withdrawn.');
      await load();
    } catch (withdrawError) {
      setError(withdrawError?.response?.data?.message || withdrawError?.message || 'Unable to withdraw proposal.');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadArtifact = async (artifact) => {
    try {
      const response = await axiosInstance.post(
        `/requests/need-opportunities/${encodeURIComponent(requestId)}/artifacts/${encodeURIComponent(artifact.file_id)}/download`,
      );
      const url = response.data?.download_url || response.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      setError(downloadError?.response?.data?.message || downloadError?.message || 'Unable to download artifact.');
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header breadcrumbItems={[
            { label: 'AMX xConsole', href: '/' },
            { label: 'Requests', href: '/requests' },
            { label: 'Need-Based Opportunities', href: '/requests' },
            { label: request.title || 'Opportunity' },
          ]} />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center"><CircularProgress size={24} /><Typography>Loading opportunity...</Typography></Stack>
          ) : error && !detail ? (
            <Alert severity="error">{error}</Alert>
          ) : detail ? (
            <Stack spacing={3} sx={{ maxWidth: 1120 }}>
              <ClassificationBanner isCui={Boolean(metadata.is_cui)} />
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" color="primary">Need-Based Opportunity</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{request.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {businessUnitLabel(detail.requesting_business_unit)} · {detail.need_category?.category_display_name || detail.need_category?.category_name}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip color="warning" label={formatCapabilityLabel(detail.status)} />
                  <Button component={RouterLink} to="/requests" startIcon={<ArrowBackRoundedIcon />}>Requests</Button>
                </Stack>
              </Stack>
              {error ? <Alert severity="error">{error}</Alert> : null}
              {feedback ? <Alert severity="success">{feedback}</Alert> : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Requested Outcome</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{request.description}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {metadata.needed_by ? <Chip size="small" label={`Needed by ${metadata.needed_by}`} /> : null}
                    {metadata.priority ? <Chip size="small" label={`Priority: ${formatCapabilityLabel(metadata.priority)}`} /> : null}
                    <Chip size="small" label={`Submitted ${formatTimestamp(request.created_at)}`} />
                  </Stack>
                  {metadata.general_notes ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{metadata.general_notes}</Typography> : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Need Requirements</Typography>
                <Table size="small">
                  <TableBody>
                    {fields.map((field) => (
                      <TableRow key={fieldName(field)}>
                        <TableCell sx={{ width: 300, fontWeight: 700 }}>{fieldLabel(field)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderValue(values[fieldName(field)])}</TableCell>
                      </TableRow>
                    ))}
                    {!fields.length ? <TableRow><TableCell><Typography color="text.secondary">No structured requirements.</Typography></TableCell></TableRow> : null}
                  </TableBody>
                </Table>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Artifacts</Typography>
                {artifacts.length ? artifacts.map((artifact) => (
                  <Stack key={artifact.file_id} direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{artifact.filename || artifact.file_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{artifact.content_type || 'file'} · {artifact.field_key}</Typography>
                    </Box>
                    <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={() => downloadArtifact(artifact)}>Download</Button>
                  </Stack>
                )) : <Typography variant="body2" color="text.secondary">No artifacts attached.</Typography>}
              </Paper>

              {proposals.length ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Your Business Units’ Proposals</Typography>
                  <Stack spacing={2}>
                    {proposals.map((proposal) => (
                      <Paper key={proposal.proposal_id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 800 }}>{businessUnitLabel(proposal.provider_business_unit)}</Typography>
                            <Chip size="small" label={formatCapabilityLabel(proposal.status)} />
                          </Stack>
                          <Typography color="primary" sx={{ fontWeight: 700 }}>
                            {proposal.capabilities.map((capability) => capability.capability_name).join(' → ')}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{proposal.explanation}</Typography>
                          {proposal.status === 'submitted' ? (
                            <Button size="small" color="error" disabled={submitting} onClick={() => withdrawProposal(proposal.proposal_id)}>
                              Withdraw Proposal
                            </Button>
                          ) : null}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Propose a Solution</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Build an outcome-driven plan from one or more Capabilities owned by your provider Business Unit.
                    </Typography>
                  </Box>
                  <TextField
                    select
                    label="Provider Business Unit"
                    value={providerBusinessUnitId}
                    onChange={(event) => setProviderBusinessUnitId(event.target.value)}
                    fullWidth
                    required
                    disabled={submitting || providerUnits.length === 1}
                  >
                    {providerUnits.map((unit) => <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>{businessUnitLabel(unit)}</MenuItem>)}
                  </TextField>
                  {providerHasSubmitted ? <Alert severity="info">This Business Unit already has a submitted proposal. Withdraw it before submitting a replacement.</Alert> : null}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Available Capabilities</Typography>
                    {capabilities.map((capability) => (
                      <FormControlLabel
                        key={capability.capability_definition_id}
                        control={(
                          <Checkbox
                            checked={selectedCapabilityIds.includes(capability.capability_definition_id)}
                            onChange={(event) => toggleCapability(capability.capability_definition_id, event.target.checked)}
                            disabled={!canSubmit || submitting}
                          />
                        )}
                        label={`${capability.capability_name}${capability.description ? ` — ${capability.description}` : ''}`}
                      />
                    ))}
                  </Box>
                  {selectedCapabilities.length ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Proposed Sequence</Typography>
                      <Stack spacing={1.5}>
                        {selectedCapabilities.map((capability, index) => (
                          <Paper key={capability.capability_definition_id} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                              <Chip color="primary" label={index + 1} />
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{capability.capability_name}</Typography>
                                <TextField
                                  label="Dependency or handoff notes (optional)"
                                  value={dependencyNotes[capability.capability_definition_id] || ''}
                                  onChange={(event) => setDependencyNotes((current) => ({ ...current, [capability.capability_definition_id]: event.target.value }))}
                                  size="small"
                                  fullWidth
                                  disabled={submitting}
                                  sx={{ mt: 1 }}
                                />
                              </Box>
                              <Stack direction="row">
                                <IconButton aria-label="Move capability up" disabled={index === 0 || submitting} onClick={() => moveCapability(index, -1)}><ArrowUpwardRoundedIcon /></IconButton>
                                <IconButton aria-label="Move capability down" disabled={index === selectedCapabilities.length - 1 || submitting} onClick={() => moveCapability(index, 1)}><ArrowDownwardRoundedIcon /></IconButton>
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                  <TextField
                    label="Proposed Approach"
                    value={explanation}
                    onChange={(event) => setExplanation(event.target.value)}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                    disabled={!canSubmit || submitting}
                    helperText="Explain how this Capability sequence fulfills the requested outcome."
                  />
                  <TextField
                    label="Overall Sequencing or Dependency Notes (Optional)"
                    value={sequencingNotes}
                    onChange={(event) => setSequencingNotes(event.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    disabled={!canSubmit || submitting}
                  />
                  <Stack direction="row" justifyContent="flex-end">
                    <Button variant="contained" startIcon={<SendRoundedIcon />} disabled={!canSubmit || submitting} onClick={submitProposal}>
                      {submitting ? 'Submitting...' : 'Submit Capability Proposal'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          ) : <Alert severity="error">Opportunity not found.</Alert>}
        </Box>
      </Box>
    </AppTheme>
  );
}
